import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TextFavorite, TextFavoriteType } from 'src/API';
import { TreatmentsService, Treatment } from 'src/app/core/remote-json/treatments/treatments.service';
import { TextFavoriteSelectComponent } from 'src/app/logged-in-navbar/clinic-setup-modal/text-favorite/text-favorite-select/text-favorite-select.component';
import { TextFavoriteService } from 'src/app/logged-in-navbar/clinic-setup-modal/text-favorite/text-favorite.service';
import { DryEyeAssessmentFormGroup } from '../../dry-eye-assessment-form/dry-eye-assessment-form.model';
import { DryEyeFormGroup } from '../../dry-eye-form/dry-eye-form.model';
import {
  DRIVER_TREATMENTS,
  UNIVERSAL_TREATMENTS,
  DriverTreatmentGroup,
  DriverTreatmentItem
} from './dews-iii-driver-treatments';

const TIER_ORDER: string[] = ['tier1', 'tier2', 'inOffice', 'advanced'];

@Component({
  selector: 'csi-dews-iii-impressions-recommendations',
  templateUrl: './dews-iii-impressions-recommendations.component.html',
  styleUrls: ['./dews-iii-impressions-recommendations.component.scss']
})
export class DewsIIIImpressionsRecommendationsComponent implements OnInit, OnDestroy {
  @Input() dewsFormGroup: DryEyeFormGroup;
  @Input() assessmentFormGroup: DryEyeAssessmentFormGroup;
  @Input() isLocked: boolean;

  get discussionSummary(): string | null {
    const scribeData = this.assessmentFormGroup && this.assessmentFormGroup.controls.aiScribeResult
      ? this.assessmentFormGroup.controls.aiScribeResult.value
      : null;
    const conv = scribeData && scribeData.conversationResult ? scribeData.conversationResult : null;
    return conv ? conv.discussionSummary : null;
  }

  readonly universalTreatments: DriverTreatmentItem[] = UNIVERSAL_TREATMENTS;
  readonly tierOrder: string[] = TIER_ORDER;
  readonly tierLabels: { [tier: string]: string } = {
    tier1: 'First-line',
    tier2: 'Second-line',
    inOffice: 'In-office',
    advanced: 'Advanced / Specialist'
  };

  public TextFavoriteType = TextFavoriteType;

  @ViewChildren(MatExpansionPanel) panels: QueryList<MatExpansionPanel>;

  activeDriverGroups: DriverTreatmentGroup[] = [];
  effectiveDedDiagnosed = false;

  constructor(
    private treatmentsService: TreatmentsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private textFavoriteService: TextFavoriteService
  ) {}

  ngOnInit() {
    this.textFavoriteService.treatmentControlMap = {
      otcEyeDrops: this.assessmentFormGroup.controls.recommendedEyeDrops,
      rxEyeDrops: this.assessmentFormGroup.controls.recommendedRxEyeDrops,
      exercises: this.assessmentFormGroup.controls.recommendedExercises,
      procedures: this.assessmentFormGroup.controls.recommendedProcedures
    };
    this.updateState();
    this.dewsFormGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.updateState());
  }

  ngOnDestroy() {}

  expandAll() {
    this.panels.forEach(function(p) { p.open(); });
  }

  closeAll() {
    this.panels.forEach(function(p) { p.close(); });
  }

  getTierTreatments(group: DriverTreatmentGroup, tier: string): DriverTreatmentItem[] {
    return group.treatments.filter(function(t) { return t.tier === tier; });
  }

  hasTierTreatments(group: DriverTreatmentGroup, tier: string): boolean {
    return this.getTierTreatments(group, tier).length > 0;
  }

  private updateState() {
    const controls = this.dewsFormGroup.controls;

    const autoDiagnosed =
      controls['dewsDedDiagnosed'] && controls['dewsDedDiagnosed'].value === true;
    const overrideActive =
      controls['dewsClinicianOverride'] && controls['dewsClinicianOverride'].value === true;
    this.effectiveDedDiagnosed = overrideActive ? !autoDiagnosed : autoDiagnosed;

    this.activeDriverGroups = DRIVER_TREATMENTS.filter(function(group) {
      const ctrl = controls[group.driverKey];
      return ctrl && ctrl.value === true;
    });

  }

  openImpressionsTextFavoriteSelect() {
    const self = this;
    this.dialog
      .open(TextFavoriteSelectComponent, { width: '90vw', data: TextFavoriteType.Impressions })
      .afterClosed()
      .subscribe(function(addedFavorites: TextFavorite[]) {
        if (addedFavorites && addedFavorites.length > 0) {
          const control = self.assessmentFormGroup.controls.impressions;
          control.setValue(
            control.value +
              (control.value ? '\n\n' : '') +
              addedFavorites.map(function(f) { return '* ' + f.text; }).join('\n\n')
          );
        }
      });
  }

  openTextFavoriteSelect() {
    var self = this;
    this.dialog
      .open(TextFavoriteSelectComponent, { width: '90vw', data: TextFavoriteType.Recommendations })
      .afterClosed()
      .subscribe(function(addedFavorites: TextFavorite[]) {
        if (addedFavorites && addedFavorites.length > 0) {
          var control = self.assessmentFormGroup.controls.recommendedPlanByDoctor;
          control.setValue(
            control.value +
              (control.value ? '\n\n' : '') +
              addedFavorites.map(function(f) { return '* ' + f.text; }).join('\n\n')
          );
          self.textFavoriteService.setTreatmentFormControlValue(addedFavorites);
        }
      });
  }

  addTreatments(treatmentIds: string[]) {
    const self = this;
    const addedNames: string[] = [];
    const existingNames: string[] = [];

    treatmentIds.forEach(function(id) {
      const treatment = self.treatmentsService.idToTreatmentMap.get(id);
      if (!treatment) { return; }
      const control =
        treatment.class === 'otc' ? self.assessmentFormGroup.controls.recommendedEyeDrops :
        treatment.class === 'rx' ? self.assessmentFormGroup.controls.recommendedRxEyeDrops :
        treatment.class === 'exercise' ? self.assessmentFormGroup.controls.recommendedExercises :
        treatment.class === 'procedure' ? self.assessmentFormGroup.controls.recommendedProcedures :
        null;
      if (!control) { return; }
      const current: Treatment[] = control.value || [];
      if (current.some(function(t) { return t.id === treatment.id; })) {
        existingNames.push(treatment.name);
        return;
      }
      control.setValue(current.concat([treatment]));
      addedNames.push(treatment.name);
    });

    if (addedNames.length > 0) {
      self.snackBar.open(addedNames.join(', ') + ' added', null, { duration: 2000 });
    } else if (existingNames.length > 0) {
      self.snackBar.open(existingNames.join(', ') + ' already in recommendations', null, { duration: 2000 });
    }
  }
}
